import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { Alert, AlertDescription } from '../ui/alert';
import { Badge } from '../ui/badge';
import { Bell, CheckCircle2, XCircle, Send, AlertCircle } from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';
import { FoxSpinnerInline } from '../ui/FoxSpinner';

const TelegramConfig = () => {
  const [status, setStatus] = useState(null);
  const [config, setConfig] = useState(null);
  const [loading, setLoading] = useState(true);
  const [sendingTest, setSendingTest] = useState(false);
  const [sendingAlert, setSendingAlert] = useState(false);
  const [saving, setSaving] = useState(false);
  
  // Estados do formulário
  const [warningThreshold, setWarningThreshold] = useState(2000000);
  const [criticalThreshold, setCriticalThreshold] = useState(1500000);
  const [emergencyThreshold, setEmergencyThreshold] = useState(500000);
  const [alertsEnabled, setAlertsEnabled] = useState(true);

  useEffect(() => {
    loadStatus();
    loadConfig();
  }, []);

  // Função para obter token do storage (localStorage ou sessionStorage)
  const getAuthToken = () => {
    return localStorage.getItem('access_token') || sessionStorage.getItem('access_token');
  };

  const loadStatus = async () => {
    setLoading(true);
    try {
      const token = getAuthToken();
      if (!token) {
        toast.error('Sessão expirada. Faça login novamente.');
        setLoading(false);
        return;
      }
      
      const response = await axios.get(`${process.env.REACT_APP_BACKEND_URL}/api/telegram/status`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setStatus(response.data);
    } catch (error) {
      console.error('Erro ao carregar status:', error);
      if (error.response?.status === 401) {
        toast.error('Sessão expirada. Faça login novamente.');
      } else {
        toast.error('Erro ao carregar configurações do Telegram');
      }
    } finally {
      setLoading(false);
    }
  };

  const loadConfig = async () => {
    try {
      const token = getAuthToken();
      if (!token) return;
      
      const response = await axios.get(`${process.env.REACT_APP_BACKEND_URL}/api/telegram/config`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      const cfg = response.data;
      setConfig(cfg);
      setWarningThreshold(cfg.warning_threshold);
      setCriticalThreshold(cfg.critical_threshold);
      setEmergencyThreshold(cfg.emergency_threshold);
      setAlertsEnabled(cfg.enabled);
    } catch (error) {
      console.error('Erro ao carregar configuração:', error);
    }
  };

  const saveConfig = async () => {
    // Validações
    if (warningThreshold <= criticalThreshold) {
      toast.error('Limite de aviso deve ser maior que o crítico');
      return;
    }
    
    if (criticalThreshold <= emergencyThreshold) {
      toast.error('Limite crítico deve ser maior que o emergencial');
      return;
    }
    
    if (emergencyThreshold < 0) {
      toast.error('Limites devem ser positivos');
      return;
    }

    setSaving(true);
    try {
      const token = getAuthToken();
      if (!token) {
        toast.error('Sessão expirada. Faça login novamente.');
        return;
      }
      
      await axios.put(
        `${process.env.REACT_APP_BACKEND_URL}/api/telegram/config`,
        {
          warning_threshold: warningThreshold,
          critical_threshold: criticalThreshold,
          emergency_threshold: emergencyThreshold,
          enabled: alertsEnabled
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      toast.success('Configuração salva com sucesso!');
      loadConfig(); // Recarrega
    } catch (error) {
      const errorMsg = error.response?.data?.detail || 'Erro ao salvar configuração';
      toast.error(errorMsg);
    } finally {
      setSaving(false);
    }
  };

  const sendTestMessage = async () => {
    setSendingTest(true);
    try {
      const token = getAuthToken();
      if (!token) {
        toast.error('Sessão expirada. Faça login novamente.');
        setSendingTest(false);
        return;
      }
      
      const response = await axios.post(
        `${process.env.REACT_APP_BACKEND_URL}/api/telegram/test`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success(response.data.message);
    } catch (error) {
      const errorMsg = error.response?.data?.detail || 'Erro ao enviar mensagem de teste';
      toast.error(errorMsg);
    } finally {
      setSendingTest(false);
    }
  };

  const sendTestAlert = async () => {
    setSendingAlert(true);
    try {
      const token = getAuthToken();
      if (!token) {
        toast.error('Sessão expirada. Faça login novamente.');
        setSendingAlert(false);
        return;
      }
      
      const response = await axios.post(
        `${process.env.REACT_APP_BACKEND_URL}/api/telegram/test-alert`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success(response.data.message);
    } catch (error) {
      const errorMsg = error.response?.data?.detail || 'Erro ao enviar alerta de teste';
      toast.error(errorMsg);
    } finally {
      setSendingAlert(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <FoxSpinnerInline size="lg" />
      </div>
    );
  }

  const isConfigured = status?.configured;

  return (
    <div className="space-y-6">
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Bell className="text-blue-500" />
            🤖 Notificações via Telegram
          </CardTitle>
          <CardDescription>
            Configure alertas automáticos de saldo KAKO baixo
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-6">
          {/* Status */}
          <div className="p-4 border rounded-lg bg-gray-50">
            <h3 className="font-semibold mb-3">Status da Configuração</h3>
            <div className="space-y-2">
              <div className="flex items-center justify-between">
                <span className="text-sm text-gray-600">Bot Token:</span>
                {status?.has_token ? (
                  <Badge className="bg-green-600">
                    <CheckCircle2 className="w-3 h-3 mr-1" />
                    Configurado
                  </Badge>
                ) : (
                  <Badge className="bg-red-600">
                    <XCircle className="w-3 h-3 mr-1" />
                    Não Configurado
                  </Badge>
                )}
              </div>
              <div className="flex items-center justify-between">
                <span className="text-sm text-gray-600">Chat ID:</span>
                {status?.has_chat_id ? (
                  <Badge className="bg-green-600">
                    <CheckCircle2 className="w-3 h-3 mr-1" />
                    Configurado
                  </Badge>
                ) : (
                  <Badge className="bg-red-600">
                    <XCircle className="w-3 h-3 mr-1" />
                    Não Configurado
                  </Badge>
                )}
              </div>
              <div className="flex items-center justify-between pt-2 border-t">
                <span className="text-sm font-semibold">Sistema:</span>
                {isConfigured ? (
                  <Badge className="bg-green-600 text-lg">
                    <CheckCircle2 className="w-4 h-4 mr-1" />
                    ATIVO
                  </Badge>
                ) : (
                  <Badge className="bg-orange-600 text-lg">
                    <AlertCircle className="w-4 h-4 mr-1" />
                    INATIVO
                  </Badge>
                )}
              </div>
            </div>
          </div>

          {/* Configuração de Alertas */}
          <div className="p-4 border rounded-lg bg-blue-50">
            <h3 className="font-semibold mb-3 flex items-center gap-2">
              <AlertCircle className="text-blue-600" />
              Configurar Limites de Alerta
            </h3>
            
            <div className="space-y-4">
              {/* Habilitado/Desabilitado */}
              <div className="flex items-center gap-2 p-3 bg-white rounded border">
                <input
                  type="checkbox"
                  checked={alertsEnabled}
                  onChange={(e) => setAlertsEnabled(e.target.checked)}
                  className="w-4 h-4"
                />
                <label className="text-sm font-medium">
                  Alertas Habilitados
                </label>
              </div>

              {/* Aviso */}
              <div className="p-3 bg-yellow-50 border border-yellow-200 rounded">
                <label className="text-sm font-semibold text-yellow-800 mb-2 block">
                  🟡 Limite de Aviso (moedas)
                </label>
                <input
                  type="number"
                  value={warningThreshold}
                  onChange={(e) => setWarningThreshold(parseInt(e.target.value) || 0)}
                  className="w-full px-3 py-2 border rounded focus:ring-2 focus:ring-yellow-500"
                  placeholder="2000000"
                  min="0"
                />
                <p className="text-xs text-yellow-700 mt-1">
                  Envia 1 alerta quando saldo ficar abaixo deste valor
                </p>
              </div>

              {/* Crítico */}
              <div className="p-3 bg-red-50 border border-red-200 rounded">
                <label className="text-sm font-semibold text-red-800 mb-2 block">
                  🔴 Limite Crítico (moedas)
                </label>
                <input
                  type="number"
                  value={criticalThreshold}
                  onChange={(e) => setCriticalThreshold(parseInt(e.target.value) || 0)}
                  className="w-full px-3 py-2 border rounded focus:ring-2 focus:ring-red-500"
                  placeholder="1500000"
                  min="0"
                />
                <p className="text-xs text-red-700 mt-1">
                  Envia 1 alerta quando saldo ficar abaixo deste valor
                </p>
              </div>

              {/* Emergencial */}
              <div className="p-3 bg-red-100 border-2 border-red-400 rounded">
                <label className="text-sm font-semibold text-red-900 mb-2 block">
                  🚨 Limite EMERGENCIAL (moedas)
                </label>
                <input
                  type="number"
                  value={emergencyThreshold}
                  onChange={(e) => setEmergencyThreshold(parseInt(e.target.value) || 0)}
                  className="w-full px-3 py-2 border-2 border-red-300 rounded focus:ring-2 focus:ring-red-600"
                  placeholder="500000"
                  min="0"
                />
                <p className="text-xs text-red-900 mt-1 font-medium">
                  ⚠️ <strong>ALERTA A CADA TRANSAÇÃO</strong> quando saldo ficar abaixo deste valor!
                </p>
              </div>

              {/* Botão Salvar */}
              <Button
                onClick={saveConfig}
                disabled={saving}
                className="w-full bg-gradient-to-r from-purple-600 to-blue-600 hover:from-purple-700 hover:to-blue-700"
              >
                {saving ? 'Salvando...' : '💾 Salvar Configuração'}
              </Button>
            </div>
          </div>

          {/* Preview dos Limites Atuais */}
          {config && (
            <div className="p-4 border rounded-lg bg-gray-50">
              <h3 className="font-semibold mb-2">📊 Limites Configurados Atualmente</h3>
              <div className="space-y-2 text-sm">
                <div className="flex items-center justify-between">
                  <span>🟡 Aviso:</span>
                  <span className="font-bold">{config.warning_threshold.toLocaleString()} moedas</span>
                </div>
                <div className="flex items-center justify-between">
                  <span>🔴 Crítico:</span>
                  <span className="font-bold">{config.critical_threshold.toLocaleString()} moedas</span>
                </div>
                <div className="flex items-center justify-between">
                  <span>🚨 Emergência:</span>
                  <span className="font-bold text-red-600">{config.emergency_threshold.toLocaleString()} moedas</span>
                </div>
                <div className="flex items-center justify-between pt-2 border-t">
                  <span>Status:</span>
                  <Badge className={config.enabled ? "bg-green-600" : "bg-gray-600"}>
                    {config.enabled ? 'ATIVO' : 'DESATIVADO'}
                  </Badge>
                </div>
              </div>
            </div>
          )}

          {/* Testes */}
          {isConfigured ? (
            <div className="space-y-3">
              <h3 className="font-semibold">Testar Notificações</h3>
              <div className="flex flex-col sm:flex-row gap-3">
                <Button
                  onClick={sendTestMessage}
                  disabled={sendingTest}
                  className="flex-1 bg-blue-600 hover:bg-blue-700"
                >
                  {sendingTest ? (
                    'Enviando...'
                  ) : (
                    <>
                      <Send className="mr-2 h-4 w-4" />
                      Enviar Mensagem de Teste
                    </>
                  )}
                </Button>
                <Button
                  onClick={sendTestAlert}
                  disabled={sendingAlert}
                  className="flex-1 bg-orange-600 hover:bg-orange-700"
                >
                  {sendingAlert ? (
                    'Enviando...'
                  ) : (
                    <>
                      <Bell className="mr-2 h-4 w-4" />
                      Simular Alerta de Saldo
                    </>
                  )}
                </Button>
              </div>
              <p className="text-xs text-gray-500">
                Após clicar, verifique seu Telegram para ver se a mensagem foi recebida.
              </p>
            </div>
          ) : (
            <Alert className="bg-orange-50 border-orange-200">
              <AlertCircle className="h-4 w-4 text-orange-600" />
              <AlertDescription className="text-orange-800">
                <strong>Sistema não configurado.</strong> Siga o guia de configuração abaixo.
              </AlertDescription>
            </Alert>
          )}

          {/* Guia Rápido */}
          <div className="p-4 border rounded-lg bg-purple-50">
            <h3 className="font-semibold mb-3 text-purple-900">📖 Guia Rápido de Configuração</h3>
            <ol className="space-y-2 text-sm text-purple-900">
              <li><strong>1.</strong> Abra o Telegram e procure por <code className="bg-purple-200 px-1 rounded">@BotFather</code></li>
              <li><strong>2.</strong> Envie <code className="bg-purple-200 px-1 rounded">/newbot</code> e siga as instruções</li>
              <li><strong>3.</strong> Copie o <strong>TOKEN</strong> que o BotFather enviar</li>
              <li><strong>4.</strong> Procure por <code className="bg-purple-200 px-1 rounded">@userinfobot</code> para obter seu <strong>Chat ID</strong></li>
              <li><strong>5.</strong> Configure no arquivo <code className="bg-purple-200 px-1 rounded">/app/backend/.env</code>:</li>
            </ol>
            <pre className="bg-purple-900 text-purple-100 p-3 rounded mt-3 text-xs overflow-x-auto">
{`TELEGRAM_BOT_TOKEN=seu_token_aqui
TELEGRAM_ADMIN_CHAT_ID=seu_chat_id_aqui`}
            </pre>
            <p className="text-xs text-purple-700 mt-2">
              📄 <strong>Guia completo:</strong> Consulte o arquivo <code>/app/TELEGRAM_SETUP_GUIDE.md</code>
            </p>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default TelegramConfig;
